import styles from './styles/HeaderShortcuts.module.scss';
import {useContext, useEffect, useState} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import FontAwesome from "../utilities/FontAwesome";
import {conditionType, currentViewType} from "../modals/AdminModal/AdminModalTypes";
import NavigationModel from "../../utils/NavigationModel";
import {runInAction} from "mobx";
import ClickableLink from "../utilities/ClickableLink";
import WatsonApi from "../../backends/WatsonApi";
import {observer} from "mobx-react";
import NotificationManager from "../notifications/NotificationManager";
import {primaryNavHeight} from "../../utils/Settings";
import classNames from "classnames";
import {useHeroTerrace} from "../../hooks/useHeroTerrace";

function Shortcut(props: {
    shortcut: Navigation,
    deleteNavigation: (id: number) => void,
    updateNavigation: (navigation: Navigation) => void,
}) {
    const {userStore, organizationStore} = useContext(StoreContext);

    const canEdit = userStore.isEditor && props.shortcut.parent === organizationStore.currentOrganization.id;

    return <li className={styles.shortcutContainer}>
        <div className={styles.popup}>
            {canEdit && <div>
                <button title={"Delete Link"} onClick={() => props.deleteNavigation(props.shortcut.id)}>
                    <FontAwesome ariaHidden name={'fa-trash'} prefix={'fas'} />
                </button>
                <button title={"Edit Link"} onClick={() => props.updateNavigation(props.shortcut)}>
                    <FontAwesome ariaHidden name={'fa-pen'} prefix={'fas'} />
                </button>
            </div>}
        </div>
        <ClickableLink href={props.shortcut.url} aria-label={props.shortcut.title}>
            {canEdit || <div className={styles.popup}>
                <div>
                    {props.shortcut.title}
                </div>
            </div>}
            <FontAwesome fixedWidth name={props.shortcut.icon} ariaHidden={true} />
        </ClickableLink>
    </li>
}

export const HeaderShortcuts = observer((props) => {
    const {userStore, modalStore, organizationStore, interfaceStore} = useContext(StoreContext);
    const heroEnabled = !!useHeroTerrace();

    const [visible, setVisible] = useState(!heroEnabled);

    const containerClassName = classNames({
        [styles.container]: true,
        [styles.containerHeroIsEnabled]: heroEnabled,
        [styles.containerVisible]: visible,
    });

    useEffect(() => {
        // this is running in a useEffect because scrollPosition is always 0 on SSR so otherwise a reload while scrolled
        // down doesn't hydrate with the correct classNames and it doesn't update
        if (interfaceStore.scrollPosition > primaryNavHeight || !(heroEnabled)) {
            setVisible(true);
        }
    }, [interfaceStore.scrollPosition])

    const navigationCreator = async (navigation) => {
        try {
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_navigations_create({
                organization_pk: organizationStore.currentOrganization.id,
                data: {
                    title: navigation.title,
                    type: 0,
                    external_url: navigation.external_url,
                    icon: navigation.icon,
                    class_field: navigation.class_field,
                    description: navigation.description,
                    path: "",
                    enabled: navigation.enabled,
                    parent_id: navigation.parent_id,
                    organization_id: navigation.organization_id,
                },
            })
            runInAction(() => {
                organizationStore.currentOrganization.shortcuts.push(JSON.parse(response.data));
            })
            return JSON.parse(response.data);
        } catch (e) {
            NotificationManager.error("Something went wrong! Please try again.")
        }
    }

    const navigationUpdater = async (navigation) => {
        try {
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_navigations_update({
                organization_pk: organizationStore.currentOrganization.id,
                id: navigation.id,
                data: {
                    id: navigation.id,
                    title: navigation.title,
                    type: 0,
                    external_url: navigation.external_url,
                    icon: navigation.icon,
                    class_field: navigation.class_field,
                    description: navigation.description,
                    path: "",
                    enabled: navigation.enabled,
                    parent_id: navigation.parent_id,
                    organization_id: navigation.organization_id,
                },
            })
            runInAction(() => {
                organizationStore.currentOrganization.shortcuts = organizationStore.currentOrganization.shortcuts.map(link => {
                    if (link.id === navigation.id) {
                        return JSON.parse(response.data);
                    }
                    return link;
                })
            })
        } catch (e) {
            NotificationManager.error("Something went wrong! Please try again.")
        }
    }

    const deleteNavigation = async (id) => {
        if (confirm("Are you sure you want to do that?")) {
            try {
                const client = await WatsonApi();
                await client.apis.organizations.organizations_navigations_delete({
                    organization_pk: organizationStore.currentOrganization.id,
                    id,
                });
                runInAction(() => {
                    organizationStore.currentOrganization.shortcuts = organizationStore.currentOrganization.shortcuts.filter(l => l.id !== id);
                })
            } catch (e) {
                NotificationManager.error("Something went wrong! Please try again.")
            }
        }
    }

    const updateNavigation = (navigation) => {
        modalStore.addModal({
            type: "admin",
            condition: conditionType.EDITING,
            nodeInfo: navigation,
            navigation: [],
            parent: String(organizationStore.currentOrganization.id),
            navigationUpdater,
        })
    }

    return <div className={containerClassName}>
        <div>
            {userStore.isEditor && <button title={"Add a Shortcut"} onClick={() => {
                modalStore.addModal({
                    type: "admin",
                    condition: conditionType.ADD_TOPLINK,
                    nodeInfo: new NavigationModel(),
                    navigation: [],
                    parent: String(organizationStore.currentOrganization.id,),
                    initialView:currentViewType.shortcut,
                    navigationCreator,
                })
            }}>
                <FontAwesome name={"fa-plus-square"} fixedWidth prefix={"fas"} ariaHidden />
            </button>}
            {organizationStore.currentOrganization.shortcuts.length > 0 && <ul>
                {organizationStore.currentOrganization.shortcuts.map(link => {
                    return <Shortcut key={link.id} shortcut={link} updateNavigation={updateNavigation} deleteNavigation={deleteNavigation} />
                })}
            </ul>}
        </div>
    </div>
})
